/**
 * ToggleSwitch - iOS-style on/off switch for boolean preferences.
 *
 * Atomic Design role: Atom. Renders the track + sliding thumb and nothing else;
 * the caller owns the label and the state.
 *
 * Extracted from ProfilePage, which carried two byte-identical copies (the
 * notification toggles and the watched-field toggles). Both rendered a 24px-tall
 * button — the whole control was the tap target, so it fell well under the 44px
 * touch minimum.
 *
 * The fix keeps the switch looking the same: the button is padded out to a 44px
 * hit area on touch viewports while the visible track stays 24px, so nothing
 * moves on screen but the thing you can actually hit is twice as tall.
 *
 * @example
 * <ToggleSwitch checked={emailAlerts} onChange={() => setEmailAlerts((v) => !v)} label="Email alerts" />
 */
import React from 'react';

export interface ToggleSwitchProps {
	/** Current on/off state. */
	checked: boolean;
	/** Called when the user flips the switch. */
	onChange: () => void;
	/** Accessible name — required, since the control renders no text of its own. */
	label: string;
	/** Disables interaction and dims the track. */
	disabled?: boolean;
	/** Optional extra classes merged onto the button. */
	className?: string;
}

export const ToggleSwitch: React.FC<ToggleSwitchProps> = ({ checked, onChange, label, disabled = false, className = '' }) => (
	<button
		type="button"
		role="switch"
		aria-checked={checked}
		aria-label={label}
		disabled={disabled}
		onClick={onChange}
		data-ref="atom-toggle-switch"
		className={[
			'toggle-switch',
			checked ? 'toggle-switch--on' : '',
			disabled ? 'toggle-switch--disabled' : '',
			/* The button is the hit area, the inner track is the visual. min-h-44px
			   grows the former on touch without resizing the latter. */
			'inline-flex items-center justify-center min-h-44px desktop:min-h-0 rounded-full',
			'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-offset-2',
			disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
			className,
		].filter(Boolean).join(' ')}
	>
		<span
			className={[
				'toggle-switch__track',
				'relative inline-flex h-24px w-44px items-center rounded-full transition-colors',
				checked ? 'bg-brand-primary' : 'bg-gray-300',
			].join(' ')}
		>
			<span
				className={[
					'toggle-switch__thumb',
					'inline-block h-20px w-20px transform rounded-full bg-white shadow transition-transform',
					checked ? 'translate-x-[22px]' : 'translate-x-[2px]',
				].join(' ')}
			/>
		</span>
	</button>
);

export default ToggleSwitch;
